// Extracted from CaseStudyCyberSecurity.jsx to reduce component complexity.
// Bilingual content (DE/EN) for the IT-Security Awareness case study page.

export const seoContent = {
  de: {
    title: 'Case Study IT-Security Awareness Training - Cyber Security Schulung',
    description: 'Praxisbeispiel: Wie ein internationales Unternehmen IT-Security Awareness Training eingeführt hat. Mehrsprachig, Compliance-konform, Fit4Cyber. Für Audits und Versicherungen.',
    keywords: 'Case Study IT-Security Awareness, Cyber Security Training Praxisbeispiel, Fit4Cyber, IT-Security Schulung, Security Awareness international, Compliance Training Cyber Security, DSGVO Schulung'
  },
  en: {
    title: 'Case Study IT-Security Awareness Training - Cyber Security Training',
    description: 'Practical example: How an international company implemented IT-Security awareness training. Multilingual, compliance-ready, Fit4Cyber. For audits and insurance.',
    keywords: 'Case Study IT-Security Awareness, Cyber Security Training practical example, Fit4Cyber, IT-Security training, Security Awareness international, Compliance Training Cyber Security, GDPR Training'
  }
};
export const content = {
  de: {
    title: 'Case Study',
    subtitle: 'IT-Security Awareness Training für internationale Unternehmensstandorte',
    backLink: 'Zurück zum Portfolio',
    
    situation: {
      title: 'Ausgangssituation',
      text: 'Ein international tätiges Unternehmen mit mehreren Standorten wollte das Sicherheitsbewusstsein seiner Mitarbeiter im Bereich IT-Security und Cyber Security nachhaltig verbessern. Cyberangriffe stellen heute eines der größten Risiken für Unternehmen dar. Gleichzeitig verlangen immer mehr Cyber Security Versicherungen, dass Mitarbeiter regelmäßig im Bereich IT-Security geschult werden. Zusätzlich bestehen Anforderungen aus verschiedenen Compliance-Richtlinien, internen Sicherheitsvorgaben und IT-Standards.',
      subtitle: 'Das Unternehmen suchte daher eine Lösung, um:',
      points: [
        'Alle Mitarbeiter regelmäßig im Bereich IT-Security zu schulen',
        'Internationale Standorte einheitlich abzudecken',
        'Schulungen in mehreren Sprachen bereitzustellen',
        'Den Schulungsstatus zentral zu dokumentieren',
        'Nachweise für Audits und Versicherungen zu erstellen'
      ]
    },
    
    challenge: {
      title: 'Herausforderung',
      text: 'Bei mehreren internationalen Standorten war es organisatorisch schwierig, Awareness-Schulungen einheitlich umzusetzen. Gleichzeitig sollte sichergestellt werden, dass:',
      points: [
        'Neue Mitarbeiter automatisch geschult werden',
        'Schulungen regelmäßig wiederholt werden',
        'Führungskräfte jederzeit einen Überblick über den Schulungsstand ihrer Teams haben',
        'Schulungen auditfähig dokumentiert sind'
      ],
      extra: 'Darüber hinaus sollte die Lösung möglichst nahtlos in die bestehende IT-Infrastruktur integriert werden.'
    },
    
    solution: {
      title: 'Lösung',
      text: 'Gemeinsam mit e-Matrix wurde ein strukturiertes Awareness-Schulungsprogramm auf Basis der Fit4Cyber e-Training Module eingeführt.',
      points: [
        'Schulungen über das digitale Unterweisungssystem bereitgestellt',
        'Verfügbar für Mitarbeiter an allen internationalen Standorten',
        'E-Trainings in mehreren Sprachvarianten verfügbar',
        'Flexible Online-Absolvierung möglich'
      ]
    },

    integration: {
      title: 'Integration in die IT-Infrastruktur',
      text: 'Für eine effiziente Benutzerverwaltung wurde das System über einen Active Directory Connector mit der bestehenden Microsoft-Umgebung des Unternehmens verbunden. Dadurch konnten mehrere Prozesse automatisiert werden:',
      points: [
        'Mitarbeiter können sich mit ihrem Microsoft-Login (Single Sign-On) anmelden',
        'Benutzer werden automatisch aus dem Active Directory importiert',
        'Teams und Organisationsstrukturen werden automatisch übernommen',
        'Änderungen im Active Directory werden automatisch synchronisiert'
      ],
      groupConnector: {
        title: 'Group AD Connector',
        text: 'Zusätzlich wurde ein Group AD Connector eingesetzt. Dadurch werden die Teams im Schulungssystem automatisch anhand der bestehenden AD-Gruppenstruktur angelegt. Dies ermöglicht:',
        points: [
          'Automatische Kurszuweisungen an ganze Teams',
          'Klare Verantwortlichkeiten für Teamleiter',
          'Minimale administrative Aufwände'
        ]
      }
    },

    trainingConcept: {
      title: 'Struktur des Schulungskonzepts',
      text: 'Das Awareness-Programm wurde als mehrjähriger Schulungszyklus aufgebaut.',
      year1: {
        title: 'Jahr 1 – Fit4Cyber',
        text: 'Im ersten Jahr absolvieren alle Mitarbeiter das umfassende Grundlagentraining Fit4Cyber. Dieses Training vermittelt unter anderem:',
        points: [
          'Typische Cyber-Angriffe',
          'Phishing-Risiken',
          'Sichere Passwortnutzung',
          'Verhalten bei verdächtigen E-Mails',
          'Sichere Nutzung von IT-Systemen'
        ]
      },
      year2: {
        title: 'Jahr 2 – Fit4Cyber Recap',
        text: 'Im zweiten Jahr absolvieren Mitarbeiter das kompaktere Fit4Cyber Recap Training, das die wichtigsten Inhalte auffrischt.'
      },
      automatic: {
        title: 'Automatische Wiederholung bei Wissenslücken',
        text: 'Wenn Mitarbeiter das Recap Training nicht erfolgreich abschließen, wird automatisch wieder das umfangreichere Fit4Cyber Grundlagentraining zugewiesen. Dadurch wird sichergestellt, dass alle Mitarbeiter den erforderlichen Wissensstand erreichen.'
      }
    },

    international: {
      title: 'Internationale Umsetzung',
      text: 'Die Awareness-Schulungen wurden für mehrere internationale Standorte eingeführt. Die Trainings stehen in mehreren Sprachvarianten zur Verfügung, wodurch ein einheitlicher Awareness-Standard für alle Mitarbeiter weltweit sichergestellt werden kann.'
    },

    transparency: {
      title: 'Transparenz für Führungskräfte',
      text: 'Über das Unterweisungssystem erhalten Führungskräfte jederzeit eine Übersicht über den Schulungsstatus ihrer Teams. Das System bietet dafür verschiedene Funktionen:',
      points: [
        'Statistische Auswertungen zum Schulungsfortschritt',
        'Automatische Benachrichtigungen bei offenen Schulungen',
        'Erinnerungen an Mitarbeiter',
        'Übersichten für Teamleiter'
      ],
      conclusion: 'Dadurch können Verantwortliche jederzeit nachvollziehen, ob ihre Teams die erforderlichen Schulungen absolviert haben.'
    },

    reporting: {
      title: 'Reporting und Auditfähigkeit',
      text: 'Das System erstellt automatisch Auswertungen über den Schulungsstatus. Diese Reports können beispielsweise verwendet werden für:',
      points: [
        'Interne Compliance-Kontrollen',
        'IT-Security-Audits',
        'Nachweise gegenüber Cyber Security Versicherungen',
        'Managementberichte'
      ],
      conclusion: 'Am Ende eines Schulungszyklus kann ein Audit- bzw. Abschlussbericht erstellt werden, der den Schulungsstatus des gesamten Unternehmens dokumentiert.'
    },
    
    result: {
      title: 'Ergebnis',
      text: 'Durch die Einführung des Awareness-Programms konnte das Unternehmen mehrere Ziele erreichen:',
      points: [
        'Einheitliches IT-Security Bewusstsein bei allen Mitarbeitern',
        'Regelmäßige Awareness-Schulungen für internationale Standorte',
        'Automatisierte Benutzerverwaltung über Active Directory',
        'Klare Übersicht über den Schulungsstatus',
        'Vollständige Dokumentation für Audits und Versicherungen'
      ]
    },
    
    comparison: {
      title: 'Ergebnis im Überblick',
      before: {
        title: 'Vorher',
        points: [
          'Unregelmäßige Awareness-Schulungen',
          'Hoher organisatorischer Aufwand',
          'Wenig Transparenz über Schulungsstände',
          'Manuelle Benutzerverwaltung'
        ]
      },
      after: {
        title: 'Nachher',
        points: [
          'Strukturierter Awareness-Schulungsplan',
          'Internationale Schulungen in mehreren Sprachen',
          'Automatische Benutzerverwaltung über Active Directory',
          'Klare Reports für Audits und Versicherungen',
          'Übersicht für Teamleiter und Management'
        ]
      }
    },
    
    cta: {
      title: 'Ähnliche Herausforderungen?',
      text: 'Kontaktieren Sie uns für eine individuelle Beratung und erfahren Sie, wie wir auch Ihr Unternehmen unterstützen können.',
      button: 'Kontakt aufnehmen',
      catalogLink: 'Zum Produktkatalog'
    }
  },
  en: {
    title: 'Case Study',
    subtitle: 'IT-Security Awareness Training for International Company Locations',
    backLink: 'Back to Portfolio',
    
    situation: {
      title: 'Initial Situation',
      text: 'An internationally operating company with multiple locations wanted to sustainably improve the security awareness of its employees in the area of IT security and cyber security. Cyberattacks represent one of the greatest risks for companies today. At the same time, more and more cyber security insurances require employees to be regularly trained in IT security. Additionally, there are requirements from various compliance guidelines, internal security regulations, and IT standards.',
      subtitle: 'The company therefore sought a solution to:',
      points: [
        'Regularly train all employees in IT security',
        'Uniformly cover international locations',
        'Provide training in multiple languages',
        'Centrally document the training status',
        'Create evidence for audits and insurance'
      ]
    },
    
    challenge: {
      title: 'Challenge',
      text: 'With multiple international locations, it was organizationally difficult to consistently implement awareness training. At the same time, it should be ensured that:',
      points: [
        'New employees are automatically trained',
        'Training is repeated regularly',
        'Managers always have an overview of their teams\' training status',
        'Training is documented in an auditable manner'
      ],
      extra: 'In addition, the solution should be integrated as seamlessly as possible into the existing IT infrastructure.'
    },
    
    solution: {
      title: 'Solution',
      text: 'Together with e-Matrix, a structured awareness training program based on the Fit4Cyber e-Training modules was introduced.',
      points: [
        'Training provided via the digital instruction system',
        'Available for employees at all international locations',
        'E-trainings available in multiple language variants',
        'Flexible online completion possible'
      ]
    },

    integration: {
      title: 'Integration into IT Infrastructure',
      text: 'For efficient user management, the system was connected to the company\'s existing Microsoft environment via an Active Directory Connector. This enabled several processes to be automated:',
      points: [
        'Employees can log in with their Microsoft login (Single Sign-On)',
        'Users are automatically imported from Active Directory',
        'Teams and organizational structures are automatically adopted',
        'Changes in Active Directory are automatically synchronized'
      ],
      groupConnector: {
        title: 'Group AD Connector',
        text: 'Additionally, a Group AD Connector was used. This automatically creates teams in the training system based on the existing AD group structure. This enables:',
        points: [
          'Automatic course assignments to entire teams',
          'Clear responsibilities for team leaders',
          'Minimal administrative effort'
        ]
      }
    },

    trainingConcept: {
      title: 'Training Concept Structure',
      text: 'The awareness program was structured as a multi-year training cycle.',
      year1: {
        title: 'Year 1 – Fit4Cyber',
        text: 'In the first year, all employees complete the comprehensive Fit4Cyber basic training. This training covers among others:',
        points: [
          'Typical cyber attacks',
          'Phishing risks',
          'Secure password usage',
          'Behavior with suspicious emails',
          'Secure use of IT systems'
        ]
      },
      year2: {
        title: 'Year 2 – Fit4Cyber Recap',
        text: 'In the second year, employees complete the more compact Fit4Cyber Recap Training, which refreshes the most important content.'
      },
      automatic: {
        title: 'Automatic Repetition for Knowledge Gaps',
        text: 'If employees do not successfully complete the Recap Training, the more comprehensive Fit4Cyber basic training is automatically reassigned. This ensures that all employees achieve the required level of knowledge.'
      }
    },

    international: {
      title: 'International Implementation',
      text: 'The awareness training was introduced for multiple international locations. The trainings are available in multiple language variants, ensuring a uniform awareness standard for all employees worldwide.'
    },

    transparency: {
      title: 'Transparency for Managers',
      text: 'Through the instruction system, managers always have an overview of their teams\' training status. The system offers various functions for this:',
      points: [
        'Statistical evaluations of training progress',
        'Automatic notifications for outstanding training',
        'Reminders for employees',
        'Overviews for team leaders'
      ],
      conclusion: 'This allows those responsible to track at any time whether their teams have completed the required training.'
    },

    reporting: {
      title: 'Reporting and Audit Capability',
      text: 'The system automatically creates evaluations of the training status. These reports can be used for example for:',
      points: [
        'Internal compliance controls',
        'IT security audits',
        'Evidence for cyber security insurance',
        'Management reports'
      ],
      conclusion: 'At the end of a training cycle, an audit or final report can be created that documents the training status of the entire company.'
    },
    
    result: {
      title: 'Result',
      text: 'By introducing the awareness program, the company was able to achieve several goals:',
      points: [
        'Uniform IT security awareness among all employees',
        'Regular awareness training for international locations',
        'Automated user management via Active Directory',
        'Clear overview of training status',
        'Complete documentation for audits and insurance'
      ]
    },
    
    comparison: {
      title: 'Results at a Glance',
      before: {
        title: 'Before',
        points: [
          'Irregular awareness training',
          'High organizational effort',
          'Little transparency about training status',
          'Manual user management'
        ]
      },
      after: {
        title: 'After',
        points: [
          'Structured awareness training plan',
          'International training in multiple languages',
          'Automatic user management via Active Directory',
          'Clear reports for audits and insurance',
          'Overview for team leaders and management'
        ]
      }
    },
    
    cta: {
      title: 'Facing Similar Challenges?',
      text: 'Contact us for individual consultation and learn how we can support your company too.',
      button: 'Contact Us',
      catalogLink: 'To Product Catalog'
    }
  }
};

